import React, { useRef, useLayoutEffect, useState } from 'react';
import { Text, Flex } from '@vibe/core';
import type { WorkloadEntry } from '../../stores/workloadStore';
import type { WorkloadDisplayMode } from '../../stores/workloadStore';
import { getWorkloadColor, WORKLOAD_COLORS } from '../../utils/colors';
import { format, parseISO } from 'date-fns';
import { formatHoursMinutes } from '../../utils/timesheet-capacity';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AggregatedEntry {
  label: string;
  scheduledHours: number;
  capacityHours: number;
  isHoliday: boolean;
  isLeave: boolean;
  leaveDayType: 'full' | 'am' | 'pm' | null;
  availableHours: number;
  /** True when items exist on a full-day leave (hours redistributed but conflict persists) */
  hasLeaveConflict: boolean;
  /** Hours logged via worklogs (for remaining_estimate / worklog_workload modes) */
  worklogHours?: number;
  /** True for past days in remaining_estimate mode */
  isPastDay?: boolean;
}

interface CapacityDetailPopupProps {
  /** User display name */
  userName: string;
  /** Aggregated entries (days or months depending on view) */
  aggregatedEntries: AggregatedEntry[];
  /** Display mode: absolute hours or percentage */
  displayMode: WorkloadDisplayMode;
  /** Active indicator type for mode-specific labels */
  indicatorType?: string | null;
  /** Position relative to the trigger element */
  anchorRect: { top: number; left: number };
  /** Called when mouse enters the popup (cancels pending close) */
  onMouseEnter?: () => void;
  /** Called when mouse leaves the popup (starts close timer) */
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Popup showing capacity breakdown per user.
 * Shows per-day details in week/month views, per-month in year views.
 * Appears when hovering over workload bars in the resource header.
 */
const CapacityDetailPopup: React.FC<CapacityDetailPopupProps> = ({
  userName,
  aggregatedEntries,
  displayMode,
  indicatorType,
  anchorRect,
  onMouseEnter,
  onClose,
}) => {
  const totalScheduled = aggregatedEntries.reduce((sum, e) => sum + e.scheduledHours, 0);
  const totalAvailable = aggregatedEntries.reduce((sum, e) => sum + e.availableHours, 0);
  const utilization = totalAvailable > 0 ? totalScheduled / totalAvailable : 0;

  const popupRef = useRef<HTMLDivElement>(null);
  const [adjustedTop, setAdjustedTop] = useState(anchorRect.top);

  useLayoutEffect(() => {
    const el = popupRef.current;
    if (!el) return;
    const popupHeight = el.getBoundingClientRect().height;
    const viewportHeight = window.innerHeight;
    if (anchorRect.top + popupHeight > viewportHeight) {
      // Flip above: position so bottom of popup aligns with original anchor top minus gap
      setAdjustedTop(Math.max(4, anchorRect.top - popupHeight - 8));
    } else {
      setAdjustedTop(anchorRect.top);
    }
  }, [anchorRect.top]);

  return (
    <div
      ref={popupRef}
      className="rp-capacity-popup"
      style={{
        position: 'fixed',
        top: adjustedTop,
        left: anchorRect.left,
        zIndex: 1000,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onClose}
    >
      <div className="rp-capacity-popup__header">
        <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
          {userName}
        </Text>
        <Text type={Text.types.TEXT2}>
          {formatHoursMinutes(totalScheduled)} / {formatHoursMinutes(totalAvailable)}
          ({(utilization * 100).toFixed(0)}%)
        </Text>
      </div>

      <div className="rp-capacity-popup__rows">
        {aggregatedEntries.map((entry, idx) => {
          const color = getWorkloadColor(
            entry.scheduledHours,
            entry.availableHours,
            entry.isLeave,
            entry.leaveDayType,
            entry.hasLeaveConflict,
            entry.isHoliday,
            entry.isPastDay,
            indicatorType ?? undefined,
            entry.worklogHours,
          );
          const pct = entry.availableHours > 0
            ? (entry.scheduledHours / entry.availableHours) * 100
            : 0;

          let label: string;
          if ((indicatorType === 'remaining_estimate' || indicatorType === 'availability') && entry.isPastDay) {
            label = '\u2013';
          } else if (indicatorType === 'worklog_workload' && entry.isPastDay) {
            label = displayMode === 'hours'
              ? `Logged: ${formatHoursMinutes(entry.scheduledHours)} / ${formatHoursMinutes(entry.availableHours)}`
              : `${pct.toFixed(0)}%`;
          } else if (indicatorType === 'availability') {
            const availRemaining = entry.availableHours - entry.scheduledHours;
            const availPct = entry.availableHours > 0
              ? (availRemaining / entry.availableHours) * 100
              : 0;
            label = displayMode === 'hours'
              ? `Available: ${formatHoursMinutes(availRemaining)} / ${formatHoursMinutes(entry.availableHours)}`
              : `${availPct.toFixed(0)}%`;
          } else if (indicatorType === 'worklog_workload' && (entry.worklogHours ?? 0) > 0 && !entry.isPastDay) {
            // Today in worklog_workload: scheduledHours = worklogs + remaining, show both
            const wlHrs = entry.worklogHours ?? 0;
            const remaining = entry.scheduledHours - wlHrs;
            label = displayMode === 'hours'
              ? `Logged: ${formatHoursMinutes(wlHrs)} | Remaining: ${remaining === 0 ? '0h' : formatHoursMinutes(remaining)} / ${formatHoursMinutes(entry.availableHours)}`
              : `${pct.toFixed(0)}%`;
          } else if (indicatorType === 'remaining_estimate' || indicatorType === 'worklog_workload') {
            label = displayMode === 'hours'
              ? `Remaining: ${formatHoursMinutes(entry.scheduledHours)} / ${formatHoursMinutes(entry.availableHours)}`
              : `${pct.toFixed(0)}%`;
          } else {
            label = displayMode === 'hours'
              ? `${formatHoursMinutes(entry.scheduledHours)} / ${formatHoursMinutes(entry.availableHours)}`
              : `${pct.toFixed(0)}%`;
          }

          return (
            <Flex
              key={idx}
              align={Flex.align.CENTER}
              justify={Flex.justify.SPACE_BETWEEN}
              className="rp-capacity-popup__day-row"
            >
              <Flex align={Flex.align.CENTER} gap={Flex.gaps.XS}>
                <Text type={Text.types.TEXT2}>{entry.label}</Text>
                {entry.isHoliday && (
                  <Text
                    type={Text.types.TEXT2}
                    style={{
                      fontSize: '10px',
                      color: WORKLOAD_COLORS.holiday,
                      fontWeight: 500,
                    }}
                  >
                    {entry.scheduledHours > 0
                      ? '\u26A0 Holiday conflict'
                      : 'Holiday'}
                  </Text>
                )}
                {entry.isLeave && !entry.isHoliday && (
                  <Text
                    type={Text.types.TEXT2}
                    style={{
                      fontSize: '10px',
                      color: (entry.leaveDayType === 'full' && (entry.scheduledHours > 0 || entry.hasLeaveConflict))
                        ? WORKLOAD_COLORS.conflict
                        : WORKLOAD_COLORS.vacation,
                      fontWeight: 500,
                    }}
                  >
                    {entry.leaveDayType === 'full' && (entry.scheduledHours > 0 || entry.hasLeaveConflict)
                      ? '\u26A0 Leave conflict'
                      : entry.leaveDayType === 'full' ? 'Leave' : 'Half-day'}
                  </Text>
                )}
              </Flex>
              <Flex align={Flex.align.CENTER} gap={Flex.gaps.XS}>
                <div
                  className="rp-capacity-popup__dot"
                  style={{ backgroundColor: color }}
                />
                <Text type={Text.types.TEXT2}>{label}</Text>
              </Flex>
            </Flex>
          );
        })}
      </div>
    </div>
  );
};

// ─── Helpers to aggregate entries ─────────────────────────────────────────────

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Aggregate workload entries to per-day rows (for week/month views) */
export function aggregateByDay(entries: WorkloadEntry[]): AggregatedEntry[] {
  return entries.map((entry) => {
    const dayDate = parseISO(entry.date);
    const dayIdx = (dayDate.getDay() + 6) % 7; // Mon=0 … Sun=6
    const dayName = DAY_NAMES[dayIdx] ?? entry.date;
    const dateLabel = format(dayDate, 'd');
    return {
      label: `${dayName} ${dateLabel}`,
      scheduledHours: entry.scheduledHours,
      capacityHours: entry.capacityHours,
      isHoliday: entry.isHoliday,
      isLeave: entry.isLeave,
      leaveDayType: entry.leaveDayType,
      availableHours: entry.availableHours,
      hasLeaveConflict: entry.hasLeaveConflict,
      worklogHours: entry.worklogHours,
      isPastDay: entry.isPastDay,
    };
  });
}

/** Aggregate workload entries to per-month rows (for year views) */
export function aggregateByMonth(entries: WorkloadEntry[]): AggregatedEntry[] {
  const monthMap = new Map<number, {
    scheduled: number;
    capacity: number;
    available: number;
    worklogHours: number;
  }>();

  for (const entry of entries) {
    const d = parseISO(entry.date);
    const monthIdx = d.getMonth();
    const existing = monthMap.get(monthIdx) ?? {
      scheduled: 0, capacity: 0, available: 0, worklogHours: 0,
    };
    existing.scheduled += entry.scheduledHours;
    existing.capacity += entry.capacityHours;
    existing.available += entry.availableHours;
    existing.worklogHours += entry.worklogHours ?? 0;
    monthMap.set(monthIdx, existing);
  }

  const result: AggregatedEntry[] = [];
  for (const [monthIdx, data] of monthMap) {
    // In the monthly aggregate, don't propagate leave flags — even a single
    // leave day would paint the entire month blue.  The availableHours total
    // already accounts for leave deductions, so the standard utilisation
    // colour logic gives the correct result without special-casing leaves.
    result.push({
      label: MONTH_NAMES[monthIdx],
      scheduledHours: Math.round(data.scheduled * 100) / 100,
      capacityHours: Math.round(data.capacity * 100) / 100,
      isHoliday: false,
      isLeave: false,
      leaveDayType: null,
      availableHours: Math.round(data.available * 100) / 100,
      hasLeaveConflict: false,
      worklogHours: data.worklogHours > 0 ? Math.round(data.worklogHours * 100) / 100 : undefined,
    });
  }

  return result.sort((a, b) => MONTH_NAMES.indexOf(a.label) - MONTH_NAMES.indexOf(b.label));
}

export default CapacityDetailPopup;
